import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { FaUsers, FaLaptopCode, FaNetworkWired, FaRocket, FaCode, FaBrain, FaLightbulb } from "react-icons/fa";
import { motion } from "framer-motion";
import { FaComputer } from "react-icons/fa6";
import { Code, Terminal, Security } from "@mui/icons-material";

// Images
import Bg from "../Images/reg.jpg";
import SampleImage from "../Images/ict.jpg";
import EventImage from "../Images/event1.jpg";
import EventImage2 from "../Images/event2.jpg";
import EventImage3 from "../Images/event3.jpg";
import event4 from "../Images/about.jpg";
import event5 from "../Images/contact.jpg";

// Tech Motivation Quotes
const techQuotes = [
  {
    quote: "The only way to do great work is to love what you do.",
    author: "Steve Jobs",
    icon: <FaLightbulb size={30} />,
  },
  {
    quote: "Innovation distinguishes between a leader and a follower.",
    author: "Steve Jobs",
    icon: <FaRocket size={30} />,
  },
  {
    quote: "Code is like humor. When you have to explain it, it's bad.",
    author: "Cory House",
    icon: <FaCode size={30} />,
  },
  {
    quote: "First, solve the problem. Then, write the code.",
    author: "John Johnson",
    icon: <FaBrain size={30} />,
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12,
    },
  },
};

const floatVariants = {
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

const glowVariants = {
  animate: {
    boxShadow: [
      "0 0 20px rgba(0, 217, 255, 0.3)",
      "0 0 40px rgba(0, 217, 255, 0.5)",
      "0 0 20px rgba(0, 217, 255, 0.3)",
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClick = () => {
    navigate("/Client/registration");
  };

  // Dark glass style
  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.8)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: isMobile ? "scroll" : "fixed",
        color: "white",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: "absolute",
              width: Math.random() * 4 + 2,
              height: Math.random() * 4 + 2,
              borderRadius: "50%",
              background: "rgba(0, 217, 255, 0.5)",
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, 20],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 2,
            }}
          />
        ))}
      </Box>

      {/* Hero Section */}
      <Container
        maxWidth="xl"
        sx={{
          position: "relative",
          zIndex: 1,
          pt: { xs: 12, md: 16 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <Grid container spacing={6} alignItems="center">
            {/* Left Side - Text Content */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Chip
                  label="WELCOME TO THE FUTURE"
                  sx={{
                    mb: 3,
                    background: "linear-gradient(90deg, #00d9ff, #00b4d8)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    px: 1,
                  }}
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    background: "linear-gradient(90deg, #ffffff, #00d9ff)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    lineHeight: 1.1,
                  }}
                >
                  ICTAZ NIPA
                  <br />
                  CHAPTER
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <motion.div variants={floatVariants} animate="animate">
                    <FaComputer size={50} color="#00d9ff" />
                  </motion.div>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontWeight: 300,
                    }}
                  >
                    Building Tomorrow's Tech Leaders
                  </Typography>
                </Box>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    mb: 4,
                    fontSize: { xs: "1rem", md: "1.15rem" },
                    lineHeight: 1.8,
                    maxWidth: 500,
                  }}
                >
                  Join the most innovative student tech community. We fuel your passion
                  for technology, connect you with industry insights, and empower your
                  journey in the digital world.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      onClick={handleClick}
                      variant="contained"
                      sx={{
                        background: "linear-gradient(90deg, #00d9ff, #00b4d8)",
                        px: 4,
                        py: 1.5,
                        borderRadius: "50px",
                        fontSize: "1rem",
                        fontWeight: 600,
                        textTransform: "none",
                        boxShadow: "0 4px 20px rgba(0, 217, 255, 0.4)",
                        "&:hover": {
                          background: "linear-gradient(90deg, #00b4d8, #00d9ff)",
                          boxShadow: "0 6px 30px rgba(0, 217, 255, 0.6)",
                        },
                      }}
                    >
                      Get Started
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      onClick={() => navigate("/Client/about")}
                      variant="outlined"
                      sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: "50px",
                        fontSize: "1rem",
                        fontWeight: 600,
                        textTransform: "none",
                        borderColor: "rgba(255, 255, 255, 0.5)",
                        color: "white",
                        "&:hover": {
                          borderColor: "#00d9ff",
                          backgroundColor: "rgba(0, 217, 255, 0.1)",
                        },
                      }}
                    >
                      Learn More
                    </Button>
                  </motion.div>
                </Box>
              </motion.div>

              {/* Stats */}
              <motion.div variants={itemVariants}>
                <Box sx={{ display: "flex", gap: 4, mt: 5, flexWrap: "wrap" }}>
                  {[
                    { number: "100+", label: "Members" },
                    { number: "10+", label: "Events" },
                    { number: "5+", label: "Projects" },
                  ].map((stat, index) => (
                    <Box key={index}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: "#00d9ff",
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255, 255, 255, 0.6)" }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Grid>

            {/* Right Side - Image with Glow */}
            <Grid item xs={12} md={6}>
              <motion.div
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <motion.div variants={glowVariants} animate="animate">
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: "20px",
                      overflow: "hidden",
                      ...darkGlassStyle,
                      p: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={SampleImage}
                      alt="ICTAZ"
                      sx={{
                        width: "100%",
                        borderRadius: "15px",
                        display: "block",
                      }}
                    />
                    {/* Floating Tech Icons */}
                    <motion.div
                      style={{
                        position: "absolute",
                        top: 20,
                        right: 20,
                        background: "rgba(0, 217, 255, 0.9)",
                        borderRadius: "50%",
                        padding: 12,
                      }}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                    >
                      <Code sx={{ color: "white" }} />
                    </motion.div>
                    <motion.div
                      style={{
                        position: "absolute",
                        bottom: 30,
                        left: 20,
                        background: "rgba(0, 0, 36, 0.9)",
                        borderRadius: "50%",
                        padding: 12,
                        border: "2px solid #00d9ff",
                      }}
                      variants={floatVariants}
                      animate="animate"
                    >
                      <Terminal sx={{ color: "#00d9ff" }} />
                    </motion.div>
                  </Box>
                </motion.div>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* Tech Quotes Section */}
      <Container maxWidth="xl" sx={{ py: 8, position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              mb: 2,
              fontWeight: 700,
              color: "white",
            }}
          >
            <span style={{ color: "#00d9ff" }}>&lt;</span> Tech Wisdom{" "}
            <span style={{ color: "#00d9ff" }}>/&gt;</span>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 6,
              color: "rgba(255, 255, 255, 0.6)",
            }}
          >
            Inspiring words from tech visionaries
          </Typography>
        </motion.div>

        <Grid container spacing={3}>
          {techQuotes.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: "20px",
                    ...darkGlassStyle,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      border: "1px solid rgba(0, 217, 255, 0.5)",
                      boxShadow: "0 15px 40px rgba(0, 217, 255, 0.2)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        color: "#00d9ff",
                        mb: 2,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "rgba(255, 255, 255, 0.9)",
                        fontStyle: "italic",
                        mb: 2,
                        textAlign: "center",
                        lineHeight: 1.6,
                      }}
                    >
                      "{item.quote}"
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#00d9ff",
                        textAlign: "center",
                        fontWeight: 600,
                      }}
                    >
                      - {item.author}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Section */}
      <Container maxWidth="xl" sx={{ py: 8, position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              mb: 2,
              fontWeight: 700,
              color: "white",
            }}
          >
            What We <span style={{ color: "#00d9ff" }}>Offer</span>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 6,
              color: "rgba(255, 255, 255, 0.6)",
            }}
          >
            Empowering the next generation of tech innovators
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {[
            {
              title: "Networking",
              icon: <FaNetworkWired size={40} />,
              desc: "Connect with industry professionals, alumni, and fellow students. Build lasting relationships that will shape your career.",
              color: "#00d9ff",
            },
            {
              title: "Skill Development",
              icon: <FaLaptopCode size={40} />,
              desc: "Hands-on workshops, coding bootcamps, and technical training sessions to enhance your programming skills.",
              color: "#00d9ff",
            },
            {
              title: "Tech Events",
              icon: <FaUsers size={40} />,
              desc: "Participate in hackathons, tech talks, and competitions. Showcase your talent and win exciting prizes.",
              color: "#00ff88",
            },
            {
              title: "Cloud & Security",
              icon: <Security sx={{ fontSize: 40 }} />,
              desc: "Learn about cloud computing, cybersecurity, and modern infrastructure to stay ahead in the tech industry.",
              color: "#00d9ff",
            },
          ].map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -15 }}
              >
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: "20px",
                    background: "rgba(255, 255, 255, 0.05)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    transition: "all 0.4s ease",
                    overflow: "visible",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.1)",
                      border: `1px solid ${feature.color}40`,
                      boxShadow: `0 20px 40px ${feature.color}20`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.2 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "20px",
                          background: `linear-gradient(135deg, ${feature.color}30, ${feature.color}10)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 3,
                          border: `2px solid ${feature.color}50`,
                          color: feature.color,
                        }}
                      >
                        {feature.icon}
                      </Box>
                    </motion.div>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: "white",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255, 255, 255, 0.6)",
                        lineHeight: 1.7,
                      }}
                    >
                      {feature.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Events Section */}
      <Container maxWidth="xl" sx={{ py: 8, position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              mb: 2,
              fontWeight: 700,
              color: "white",
            }}
          >
            Recent <span style={{ color: "#00d9ff" }}>Events</span>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 6,
              color: "rgba(255, 255, 255, 0.6)",
            }}
          >
            Highlights from our tech community
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {[
            {
              title: "Tech Competition",
              image: EventImage,
              desc: "1st position at ZCAS Show & Shine with our Irrigation System Project",
              tag: "Competition",
            },
            {
              title: "Arduino Day",
              image: EventImage2,
              desc: "Arduino Day Celebration at EO Robotics with IEEE Association",
              tag: "Workshop",
            },
            {
              title: "Tech Community",
              image: EventImage3,
              desc: "ZCAS ICTAZ, Tech Trends, NIPA ICTAZ, Women in Tech collaboration",
              tag: "Networking",
            },
            {
              title: "Website Launch",
              image: event4,
              desc: "Official ICTAZ NIPA Chapter website launch celebration",
              tag: "Milestone",
            },
            {
              title: "Executive Meeting",
              image: event5,
              desc: "Planning future tech initiatives and events",
              tag: "Planning",
            },
          ].map((event, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <Card
                  sx={{
                    borderRadius: "20px",
                    overflow: "hidden",
                    ...darkGlassStyle,
                    transition: "all 0.4s ease",
                    "&:hover": {
                      boxShadow: "0 20px 50px rgba(0, 217, 255, 0.3)",
                      transform: "translateY(-5px)",
                    },
                  }}
                >
                  <Box sx={{ position: "relative", overflow: "hidden" }}>
                    <motion.div whileHover={{ scale: 1.1 }} transition={{ duration: 0.4 }}>
                      <CardMedia
                        component="img"
                        height="220"
                        image={event.image}
                        alt={event.title}
                        sx={{ objectFit: "cover" }}
                      />
                    </motion.div>
                    <Chip
                      label={event.tag}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 15,
                        left: 15,
                        background: "linear-gradient(90deg, #00d9ff, #00b4d8)",
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "white",
                        mb: 1,
                      }}
                    >
                      {event.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255, 255, 255, 0.7)",
                        lineHeight: 1.6,
                      }}
                    >
                      {event.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: 10, position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              textAlign: "center",
              borderRadius: "30px",
              p: { xs: 4, md: 6 },
              background: "linear-gradient(135deg, rgba(0, 217, 255, 0.2), rgba(0, 0, 36, 0.8))",
              border: "1px solid rgba(0, 217, 255, 0.3)",
              backdropFilter: "blur(20px)",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: "white",
              }}
            >
              Ready to Start Your Tech Journey?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                mb: 4,
                maxWidth: 500,
                mx: "auto",
              }}
            >
              Join ICTAZ NIPA Chapter today and be part of a community that shapes the future of technology.
            </Typography>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={handleClick}
                variant="contained"
                size="large"
                sx={{
                  background: "linear-gradient(90deg, #00d9ff, #00b4d8)",
                  px: 5,
                  py: 1.5,
                  borderRadius: "50px",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "0 4px 25px rgba(0, 217, 255, 0.5)",
                  "&:hover": {
                    boxShadow: "0 6px 35px rgba(0, 217, 255, 0.7)",
                  },
                }}
              >
                Join Now
              </Button>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Home;
